/**
 * Objectif de poids — saisie, consultation, et **écart au rythme visé**
 * (specs §6.6, archi §4.6).
 *
 * ⚠️ **ARBITRAGE Progression / Réglages.** Le §6.8 des specs range « objectif de
 * poids » sous Réglages ; il est ici, dans Progression. Même arbitrage qu'à
 * l'étape 5 entre Bibliothèque et Réglages, et pour la même raison : ce qu'on
 * règle ici n'a de sens qu'à côté de ce qu'il mesure. L'écart entre le rythme
 * réel et le rythme visé — la seule chose que les specs demandent d'afficher —
 * n'existe que si les deux sont sous les yeux. Un formulaire dans Réglages
 * aurait obligé à changer d'onglet pour voir l'effet de ce qu'on vient de
 * saisir. Le volet Réglages porte donc un **renvoi** vers ici, exactement comme
 * le volet planning renvoie vers la Bibliothèque.
 *
 * ⚠️ **La table est historisée : on insère, on ne modifie jamais** (§4.6).
 * Enregistrer un nouvel objectif ajoute une ligne ; les précédentes restent
 * consultables sous « objectifs précédents ». C'est pour ça qu'il n'existe pas de
 * `PATCH` côté serveur, et pas pour ça qu'on aurait oublié de l'écrire.
 *
 * ⚠️ **Refonte** : la carte est celle du prototype — titre à gauche, bouton
 * fantôme « Définir » à droite, explication en dessous. Rien de la logique n'a
 * bougé : les mêmes valeurs, la même phrase d'écart, le même historique chargé
 * à la demande.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { WeightGoalDTO, WeightSummaryDTO } from '../../../../shared/types';
import { fetchWeightGoals, WEIGHT_GOALS_KEY } from '../../api/weight';
import { Button, Card, TextField } from '../../components/nourri';
import { Hint, Sheet } from '../../components/ui/primitives';
import { toNumber } from '../../lib/formFields';
import { formatKg, formatSigned, MAX_KG, MAX_RATE_KG_WEEK, MIN_KG } from './weightFields';

function goalDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}

/**
 * Phrase l'écart entre le rythme réel et le rythme visé.
 *
 * ⚠️ **Le signe seul ne dit pas « bien » ou « mal ».** En perte, viser −0,5 et
 * tenir −0,3 donne +0,2 : on descend moins vite que prévu. En prise, le même
 * +0,2 voudrait dire qu'on monte plus vite que prévu. C'est le sens de
 * l'objectif qui décide, et c'est ce que fait cette fonction — le serveur, lui,
 * se contente de soustraire.
 *
 * ⚠️ Aucun jugement, aucune consigne : l'app décrit, elle ne corrige pas. Même
 * discipline que l'écart kcal du bandeau (specs §6.2, « ne corrige jamais, ne
 * bloque jamais, ne suggère jamais »).
 */
function describeGap(gap: number, targetRate: number): string {
  if (Math.abs(gap) < 0.05) return 'au rythme visé';
  /*
   * Maintien (rythme visé nul) : il n'y a ni « plus vite » ni « plus lentement »
   * à dire, seulement un sens de dérive. Sans ce cas à part, viser 0 et prendre
   * 200 g par semaine se serait affiché « plus vite que visé », qui ne veut rien
   * dire quand la cible est de ne pas bouger.
   */
  if (targetRate === 0) return gap > 0 ? 'dérive vers le haut' : 'dérive vers le bas';
  const slower = targetRate < 0 ? gap > 0 : gap < 0;
  return slower ? 'plus lentement que visé' : 'plus vite que visé';
}

function GoalEditor({
  current,
  saving,
  onSubmit,
  onClose,
}: {
  current: WeightGoalDTO | null;
  saving: boolean;
  onSubmit: (targetKg: number, rateKgWeek: number) => void;
  onClose: () => void;
}) {
  const [target, setTarget] = useState(current === null ? '' : formatKg(current.targetKg));
  const [rate, setRate] = useState(
    current === null ? '' : String(current.rateKgWeek).replace('.', ','),
  );

  const targetValue = toNumber(target);
  const rateValue = toNumber(rate);
  const valid =
    targetValue >= MIN_KG &&
    targetValue <= MAX_KG &&
    Math.abs(rateValue) <= MAX_RATE_KG_WEEK &&
    rate.trim() !== '';

  return (
    <Sheet open title="Objectif de poids" onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <TextField
          label="Poids cible"
          unit="kg"
          numeric
          inputMode="decimal"
          value={target}
          onChange={(event) => {
            setTarget(event.target.value);
          }}
          placeholder=""
          autoComplete="off"
        />
        <TextField
          label="Rythme visé"
          unit="kg/sem."
          numeric
          inputMode="decimal"
          value={rate}
          onChange={(event) => {
            setRate(event.target.value);
          }}
          placeholder=""
          autoComplete="off"
        />
      </div>
      <Hint>
        Le rythme est <strong>signé</strong> : −0,5 pour perdre un demi-kilo par semaine, +0,25
        pour en prendre un quart. C’est ce signe qui donne son sens à l’écart affiché.
      </Hint>
      <Hint>
        Enregistrer <strong>ajoute</strong> un objectif : le précédent reste dans l’historique,
        il n’est pas écrasé.
      </Hint>
      <Button
        variant="primary"
        shape="rounded"
        block
        disabled={!valid || saving}
        onClick={() => {
          onSubmit(targetValue, rateValue);
        }}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
    </Sheet>
  );
}

export function WeightGoalPanel({
  goal,
  summary,
  saving,
  onSubmit,
  onRemove,
}: {
  goal: WeightGoalDTO | null;
  summary: WeightSummaryDTO;
  saving: boolean;
  onSubmit: (targetKg: number, rateKgWeek: number) => void;
  onRemove: (id: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Historique chargé **à la demande** : il ne sert qu'à montrer que rien n'est
  // effacé, et le charger d'office ferait une requête de plus à chaque ouverture
  // de l'onglet pour une information qu'on regarde deux fois par an.
  const history = useQuery({
    queryKey: WEIGHT_GOALS_KEY,
    queryFn: fetchWeightGoals,
    enabled: showHistory,
  });

  const previous = (history.data ?? []).filter((row) => row.id !== goal?.id);

  return (
    <Card>
      <div className="n-row n-row--between">
        <h2 className="n-title">Objectif</h2>
        <Button
          variant="ghost"
          shape="rounded"
          onClick={() => {
            setEditing(true);
          }}
        >
          {goal === null ? 'Définir' : 'Modifier'}
        </Button>
      </div>

      {goal === null ? (
        <p className="n-hint" style={{ marginTop: 10 }}>
          Aucun objectif. Un poids cible et un rythme visé permettent d’afficher l’écart avec le
          rythme réellement tenu.
        </p>
      ) : (
        <div className="n-stack n-stack--tight" style={{ marginTop: 12 }}>
          <div className="n-goal-figures tnum">
            <span>
              Cible <strong>{formatKg(goal.targetKg)} kg</strong>
            </span>
            <span>
              Rythme visé <strong>{formatSigned(goal.rateKgWeek)} kg/sem.</strong>
            </span>
          </div>

          {/*
            L'écart, c'est-à-dire la seule chose que le §6.6 demande d'afficher.
            ⚠️ Chaque valeur est dans sa propre boîte `nowrap`, et les boîtes
            passent à la ligne plutôt que de se chevaucher : c'est le défaut
            corrigé sur les jauges de repas à l'étape 7, appliqué ici par
            avance — « −0,42 kg/sem. » est plus large que tout ce qu'affiche le
            journal.
          */}
          {summary.rateGapKgWeek === null ? (
            <p className="n-hint">
              L’écart au rythme visé s’affichera dès qu’une tendance sera disponible.
            </p>
          ) : (
            <div className="n-goal-gap">
              <span>
                Écart&nbsp;:{' '}
                <strong className="tnum">{formatSigned(summary.rateGapKgWeek)} kg/sem.</strong>
              </span>
              <span>— {describeGap(summary.rateGapKgWeek, goal.rateKgWeek)}</span>
            </div>
          )}

          {summary.toTargetKg !== null && (
            <div className="n-goal-gap">
              <span>
                Reste <strong className="tnum">{formatKg(Math.abs(summary.toTargetKg))} kg</strong>{' '}
                {summary.toTargetKg > 0 ? 'à perdre' : 'à prendre'}
              </span>
            </div>
          )}

          {/*
            🔴 **La projection au rythme actuel (V2 §6.2, étape 9) — et elle ne
            s'affiche QUE lorsqu'elle veut dire quelque chose.**

            Le §6.2 ne la publie que sous les mêmes planchers que la tendance
            (3 pesées étalées sur 7 jours) **et** à tendance non nulle : projeter
            sur une tendance nulle est une division par zéro déguisée, qui
            afficherait « jamais » ou une date absurde selon l'arrondi. S'y
            ajoute le cas que le §6.2 n'avait pas écrit et qui est bien réel — une
            tendance qui **s'éloigne** de la cible, dont la date d'arrivée
            tomberait dans le passé.

            ⚠️ **Les cinq motifs de retenue ne sont PAS affichés**, et c'est la
            même discipline qu'à la progression de charge (étape 7) : ils sont
            portés par la réponse d'API pour être diagnosticables, jamais
            commentés à l'écran. Écrire « votre tendance vous éloigne de votre
            objectif » serait un **jugement**, ce que l'application s'interdit
            depuis l'écart kcal de la V1 (specs §6.2). Une projection qui ne dit
            rien ne dit rien.

            ⚠️ **Une date, pas un nombre de semaines** : « dans 14 semaines »
            demande un calcul mental pour savoir de quel mois on parle.
          */}
          {summary.projection !== null && (
            <div className="n-goal-gap">
              <span>
                À ce rythme, objectif atteint vers le{' '}
                <strong className="tnum">{summary.projection.date}</strong>
              </span>
            </div>
          )}

          {/*
            Chevron de repli : sans lui, ce bouton se lit comme une simple ligne
            de texte gris et rien ne dit qu'on peut le toucher — constaté en
            relisant une capture. Même affordance que les repas repliables du
            journal.
          */}
          <Button
            variant="ghost"
            shape="rounded"
            aria-expanded={showHistory}
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setShowHistory((value) => !value);
            }}
          >
            {showHistory ? '▾ Masquer les objectifs précédents' : '▸ Objectifs précédents'}
          </Button>

          {showHistory && (
            <div className="n-stack n-stack--tight">
              {history.isPending && <p className="n-hint">Chargement…</p>}
              {previous.length === 0 && !history.isPending && (
                <p className="n-hint">Aucun objectif antérieur — celui-ci est le premier.</p>
              )}
              {previous.map((row) => (
                <div key={row.id} className="n-goal-past">
                  <span className="tnum">
                    {formatKg(row.targetKg)} kg · {formatSigned(row.rateKgWeek)} kg/sem. ·{' '}
                    {goalDate(row.createdAt)}
                  </span>
                  <Button
                    variant="ghost"
                    shape="rounded"
                    disabled={saving}
                    onClick={() => {
                      onRemove(row.id);
                    }}
                  >
                    Retirer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <GoalEditor
          current={goal}
          saving={saving}
          onSubmit={(targetKg, rateKgWeek) => {
            onSubmit(targetKg, rateKgWeek);
            setEditing(false);
          }}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Card>
  );
}
